/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description SearchClaimCodes Button
 */
import _ from 'lodash';
import { AutoComplete, Card, Flex, Input, InputRef, Modal, Pagination, Skeleton, Tag, Typography } from 'antd';
import { Search, Down, Right } from '@icon-park/react';
import { useMemo, useRef, useState } from 'react';

import Button from 'components/widgets/button';
import CodeHierarchy from 'components/codeExplorer/CodeHierarchy';
import useCodeSuggestions from 'hooks/codeExplorer/useCodeSuggestions';
import useSearchCodes from 'hooks/codeExplorer/useSearchCodes';
import { CODE_SET_SYSTEM_OPTIONS, CODE_STATUS_MAPPING, SHOW_HIERARCHY_CODES } from 'app-constants/codeExplorer';
import { CodeTitle, ImageWidget } from 'components/codeExplorer/CodeWidgets';
import { codeExplorerImages } from 'utils/codeExplorer/codeExplorerIcons';
import { getCodeDetailedViewPath } from 'utils/router';
import { getTruncatedText } from 'utils/codeExplorer/icdParser';
import { renderPaginationTotal } from 'lib/antd/Table';

const LookupResultsDisplay: React.FC<{
	data: CodeExplorer.ICodeSetSearchResult;
	isLoading: boolean;
	isError: boolean;
}> = ({ data, isLoading, isError }) => {
	const [expandedHierarchies, setExpandedHierarchies] = useState<string[]>([]);

	const toggleHierarchy = (codeId: string) => {
		if (_.includes(expandedHierarchies, codeId)) {
			setExpandedHierarchies(_.filter(expandedHierarchies, (id) => id !== codeId));
		} else {
			setExpandedHierarchies([...expandedHierarchies, codeId]);
		}
	};

	if (isLoading) {
		return <Skeleton active />;
	}
	if (isError) {
		return <ImageWidget imageSrc={codeExplorerImages.getNoResultFoundImage} description='Error loading results' />;
	}
	if (_.isEmpty(data?.data)) {
		return <ImageWidget imageSrc={codeExplorerImages.getNoResultFoundImage} description='No results found' />;
	}

	return (
		<Flex vertical gap={8} className='w-full'>
			{_.map(data?.data, (item, index) => {
				const isTerminated = item?.status === CODE_STATUS_MAPPING.terminated;
				const matchingCodes = item?.matchingcodes;
				return (
					<Card
						className={`code-item-card${isTerminated ? '-terminated-code' : ''}`}
						key={index}
						classNames={{ body: 'py-2 px-4' }}
					>
						<Flex vertical gap={4} className='w-full'>
							<Flex align='center' justify='space-between' gap={8}>
								<CodeTitle code={item?.displaycode} codeSet={item?.codeset} modifier={item?.ismodifier} isCopyable={true} />
								<Button.AddFavoriteCode
									code={item?.displaycode}
									codeSet={item?.codeset}
									codeId={item?.id}
									reactions={item?.tenantmetadata?.reaction}
									showOnlyIcon={true}
								/>
							</Flex>
							<Typography.Link
								href={getCodeDetailedViewPath({ code: item?.displaycode, codeSet: item?.codeset })}
								target='_blank'
							>
								{getTruncatedText(item?.shortdescription, 150)}
							</Typography.Link>
							{!isTerminated && (
								<Typography.Text type='secondary'>{getTruncatedText(item?.longdescription, 300)}</Typography.Text>
							)}
							{_.includes(SHOW_HIERARCHY_CODES, item?.codeset) && (
								<div>
									<Flex gap={16}>
										<Typography.Link
											underline
											onClick={() => {
												toggleHierarchy(item?.id);
											}}
										>
											{_.includes(expandedHierarchies, item?.id) ? (
												<span>
													<Down className='mr-2' />
													{'Hide Code Hierarchy'}
												</span>
											) : (
												<span>
													<Right className='mr-2' />
													{'View Code Hierarchy'}
												</span>
											)}
										</Typography.Link>
										{_.size(matchingCodes) > 0 && (
											<Typography.Text type='warning'>
												{_.size(matchingCodes)} matching result{_.size(matchingCodes) > 1 ? 's' : ''}
											</Typography.Text>
										)}
									</Flex>

									{_.includes(expandedHierarchies, item?.id) && (
										<div className='mt-2'>
											<CodeHierarchy
												codeId={item?.id}
												codeName={item?.displaycode}
												matchingCodes={matchingCodes}
												showDotNotation={false}
												newTab={true}
											/>
										</div>
									)}
								</div>
							)}
						</Flex>
					</Card>
				);
			})}
		</Flex>
	);
};

const LookupSearchInput: React.FC<{
	onSearch: (searchTerm: string) => void;
	codeSetSystem: CodeExplorer.ICodeSetSystem[];
}> = ({ onSearch, codeSetSystem }) => {
	const [inputValue, setInputValue] = useState<string>('');
	const [debouncedInputValue, setDebouncedInputValue] = useState<string>('');
	const inputRef = useRef<InputRef>(null);

	const codeSuggestions = useCodeSuggestions({
		searchterm: debouncedInputValue,
		codesetsystem: codeSetSystem || [],
	});

	const handleSearchChange = useMemo(
		() =>
			_.debounce((value: string) => {
				setDebouncedInputValue(value);
			}, 300),
		[],
	);

	const handleSearchSubmit = () => {
		setTimeout(() => {
			inputRef.current?.blur();
		});
		if (_.trim(inputValue)) {
			onSearch(inputValue);
		}
	};

	const options = _.map(codeSuggestions?.data?.data, (result) => ({
		value: result.id,
		data: result,
		label: (
			<Flex align='center' justify='space-between' gap={8}>
				<Flex align='center' className='search-result-item p-2'>
					<CodeTitle code={result.code} codeSet={result.codeset} isCopyable={true} modifier={result.ismodifier} />
					<Typography.Link href={getCodeDetailedViewPath({ code: result.displaycode, codeSet: result.codeset })} target='_blank'>
						{getTruncatedText(result.shortdescription, 60)}
					</Typography.Link>
				</Flex>
				<Button.AddFavoriteCode
					code={result.displaycode}
					codeSet={result.codeset}
					codeId={result.id}
					reactions={result?.tenantmetadata?.reaction}
					showOnlyIcon={true}
				/>
			</Flex>
		),
	}));

	return (
		<div className='code-autocomplete-container relative'>
			<AutoComplete
				value={inputValue}
				options={options}
				popupRender={(menu) => (
					<div className='rounded-lg bg-[#FFFFFF] p-2'>
						<Flex vertical className='w-full'>
							{codeSuggestions?.isLoading ? <Skeleton active /> : menu}
						</Flex>
					</div>
				)}
				getPopupContainer={() => document.querySelector('.code-autocomplete-container')}
				styles={{ popup: { root: { top: 40 } }, root: { width: '930px' } }}
			>
				<Input.Search
					ref={inputRef}
					enterButton
					size='large'
					placeholder='Search for a code or keyword(s)'
					data-testid='cf-code-lookup-search-input'
					value={inputValue}
					onChange={(e) => {
						setInputValue(e.target.value);
						handleSearchChange(e.target.value);
					}}
					allowClear={{
						clearIcon: (
							<Button
								type='link'
								size='small'
								className='text-danger'
								onClick={(e) => {
									e.stopPropagation();
									setInputValue('');
									setDebouncedInputValue('');
								}}
							>
								{'Clear'}
							</Button>
						),
					}}
					onPressEnter={handleSearchSubmit}
					onSearch={handleSearchSubmit}
				/>
			</AutoComplete>
		</div>
	);
};

const CodeLookupContent: React.FC<{ codeSetSystem: CodeExplorer.ICodeSetSystem[] }> = ({ codeSetSystem }) => {
	const [searchModel, setSearchModel] = useState<CodeExplorer.ICodeSetSearch>({
		searchterm: '',
		codesetsystem: codeSetSystem || [],
	});
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const searchResults = useSearchCodes(searchModel, pageNumber, pageSize);

	const handleSearchSubmit = (searchTerm: string) => {
		setSearchModel({ ...searchModel, searchterm: searchTerm });
		setPageNumber(1);
	};

	return (
		<>
			<Flex vertical gap={16} className='mb-4'>
				<LookupSearchInput onSearch={handleSearchSubmit} codeSetSystem={codeSetSystem} />
				<Flex align='center' gap={8}>
					{_.map(codeSetSystem, (codeSet) => (
						<Tag key={codeSet}>{_.find(CODE_SET_SYSTEM_OPTIONS, { value: codeSet })?.label}</Tag>
					))}
				</Flex>

				{searchModel?.searchterm ? (
					<LookupResultsDisplay
						data={searchResults?.data}
						isLoading={searchResults?.isLoading}
						isError={searchResults?.isError}
					/>
				) : (
					<ImageWidget
						imageSrc={codeExplorerImages.getNoResultFoundImage}
						description='Search for a code or keyword(s) to view results'
					/>
				)}
			</Flex>
			{searchResults.data?.count > pageSize && (
				<Pagination
					key='pagination'
					showSizeChanger
					current={pageNumber}
					pageSize={pageSize}
					showTotal={renderPaginationTotal}
					total={searchResults.data?.count}
					onChange={setPageNumber}
					onShowSizeChange={(__, newPageSize) => setPageSize(newPageSize)}
				/>
			)}
		</>
	);
};

const SearchClaimCodes: React.FC<{ codeSetSystem: CodeExplorer.ICodeSetSystem[] }> = ({ codeSetSystem }) => {
	const [modalVisible, setModalVisible] = useState<boolean>(false);

	return (
		<>
			<Button size='small' type='text' icon={<Search />} onClick={() => setModalVisible(true)} data-testid='cf-code-lookup-trigger' />

			<Modal
				title='Code Lookup'
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				footer={null}
				width={1000}
				destroyOnHidden
				maskClosable={false}
				data-testid='cf-code-lookup-modal'
				styles={{
					content: {
						height: '600px',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					},
					body: {
						flex: 1,
						overflowY: 'auto',
					},
				}}
			>
				<CodeLookupContent codeSetSystem={codeSetSystem} />
			</Modal>
		</>
	);
};

export default SearchClaimCodes;
